import React from "react";
import { T } from "../theme/tokens.js";
import { Btn } from "./ui/index.jsx";
import { COLUMN_TYPES } from "../lib/trackerViews.js";
import { keyFor } from "../lib/importers/spreadsheet.js";

const input = {
  width: "100%", padding: "8px 10px", border: `1px solid ${T.line}`,
  borderRadius: 4, fontSize: 14, background: "#fff", color: T.ink,
};

/* ------------------------------------------------------------ the columns
 *
 * A tracker made in the app has no header row to read, so the columns are
 * typed here. Same shape as an imported one, { key, label, type }, plus the
 * list of choices when there is one, so the chart engine and the row table
 * cannot tell the two apart.
 *
 * The key is generated from the label once and then left alone. Renaming a
 * column changes what it says, never where its data lives, so a rename can
 * never empty a column that already has months of rows in it.
 */
export default function ColumnsEditor({ columns, onChange, suggestOptions }) {
  const set = (i, patch) => onChange(columns.map((c, n) => (n === i ? { ...c, ...patch } : c)));

  const add = () => {
    const taken = new Set(columns.map((c) => c.key));
    onChange([...columns, { key: keyFor(`column ${columns.length + 1}`, taken),
                            label: "", type: "text" }]);
  };

  const remove = (i) => onChange(columns.filter((_, n) => n !== i));

  const move = (i, by) => {
    const to = i + by;
    if (to < 0 || to >= columns.length) return;
    const next = [...columns];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {columns.map((c, i) => (
        <div key={c.key} style={{
          border: `1px solid ${T.line}`, borderRadius: 4, padding: 10,
          display: "grid", gap: 8,
        }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              style={{ ...input, flex: "2 1 200px" }}
              placeholder="Column heading"
              value={c.label}
              onChange={(e) => set(i, { label: e.target.value })} />
            <select style={{ ...input, flex: "1 1 190px" }} value={c.type}
              onChange={(e) => {
                const type = e.target.value;
                if (type !== "choice") return set(i, { type, options: undefined });
                /* Turning an imported column into a list of choices fills the
                   list from what is already in it, so a Status column that came
                   out of a workbook becomes a dropdown without anybody retyping
                   its own values. */
                const options = c.options?.length ? c.options : (suggestOptions?.(c.key) ?? []);
                return set(i, { type, options });
              }}>
              {COLUMN_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <Btn small kind="ghost" onClick={() => move(i, -1)} disabled={i === 0}>Up</Btn>
            <Btn small kind="ghost" onClick={() => move(i, 1)}
              disabled={i === columns.length - 1}>Down</Btn>
            <Btn small kind="danger" onClick={() => remove(i)}>Remove</Btn>
          </div>

          {c.type === "choice" && (
            <input
              style={input}
              placeholder="The choices, separated by commas. Open, In review, Closed"
              value={(c.options ?? []).join(", ")}
              onChange={(e) => set(i, {
                options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
              })} />
          )}

          <div style={{ fontSize: 11.5, color: T.soft }}>
            {COLUMN_TYPES.find((t) => t.key === c.type)?.note}
          </div>
        </div>
      ))}

      <div>
        <Btn small kind="ghost" onClick={add}>Add a column</Btn>
      </div>
    </div>
  );
}
